import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Layout,
  Tabs,
  Typography,
  Spin,
  DatePicker,
  Button,
} from '@surecompanies/core_components'
import { useQueryClient, useIsFetching } from '@tanstack/react-query'
import { ReloadOutlined } from '@ant-design/icons'
import { browserHistory } from 'react-router'
import dayjs from 'dayjs'
import styled from 'styled-components'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { FINANCE_PATH } from '../../constants'
import {
  usePaymentAssignmentUserRoleQuery,
  usePaymentMetricsQuery,
} from '../../api/payment-assignment-api-client'
import { PaymentAssignmentRole } from '../../components/PaymentAssignment/Utils/roles'
import PaymentMetrics from '../../components/PaymentAssignment/Metrics/PaymentMetrics'
import CustomerMetrics from '../../components/PaymentAssignment/Metrics/CustomerMetrics'
import PaymentBotDashboardTab from '../PaymentBotDashboard/PaymentBotDashboardTab'
import DateRangeFilter, {
  isDateRangeValid,
} from '../../components/PaymentAssignment/Utils/DateRangeFilter'
import type { DateRangeValue } from '../../components/PaymentAssignment/Utils/DateRangeFilter'
import { endOfDayPST, startOfDayPST, TIMEZONE_PST } from 'helpers/datetime'
import { getDefaultPaymentPeriod } from '../../components/PaymentAssignment/hooks/usePaymentAssignmentFilters'
import type {
  DashboardView,
  CarrierIdSelection,
} from '../../components/PaymentBotDashboard/shared/types'
import {
  METRIC_SECTION,
  buildSectionExpandedMap,
  parseMetricsTab,
  parseMetricsPeriod,
  parseMetricsDateRange,
  parseMetricsQuickRange,
  parseMetricsCollapsed,
  parseMetricsShowAll,
  parseMetricsCompanies,
  parseMetricsCarriers,
  parseMetricsBotState,
  syncMetricsUrl,
} from '../../components/PaymentAssignment/Utils/metricsUrlState'

const { Content } = Layout
const { Text } = Typography

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`

const fmt = (n: number) => n.toLocaleString()

export default function PaymentAssignmentMetrics() {
  const {
    data: paymentAssignmentRoleData,
    isFetched: isRoleFetched,
    isLoading: isRoleLoading,
  } = usePaymentAssignmentUserRoleQuery({ refetchOnWindowFocus: false })

  const isCheckerOrViewer =
    paymentAssignmentRoleData?.role === PaymentAssignmentRole.CHECKER ||
    paymentAssignmentRoleData?.role === PaymentAssignmentRole.UNKNOWN

  useEffect(() => {
    if (!isRoleFetched || isRoleLoading) return
    if (!isCheckerOrViewer) {
      browserHistory.push(`${FINANCE_PATH}/payment-assignment`)
    }
  }, [isRoleFetched, isRoleLoading, isCheckerOrViewer])

  const [paymentPeriod, setPaymentPeriod] = useState<string>(parseMetricsPeriod)
  const [activeTab, setActiveTab] = useState<string>(parseMetricsTab)
  const isDateRangeTab = activeTab === 'payment' || activeTab === 'customer'

  const currentDate = useMemo(
    () => (paymentPeriod ? dayjs(paymentPeriod) : dayjs()),
    [paymentPeriod]
  )

  const handleDateChange = useCallback((d: dayjs.Dayjs | null) => {
    if (d) {
      setPaymentPeriod(d.format('YYYY-MM'))
    }
  }, [])

  const paymentAssignmentPeriod = useMemo(() => {
    const [y, m] = (paymentPeriod || getDefaultPaymentPeriod())
      .split('-')
      .map(Number)
    return { month: m - 1, year: y }
  }, [paymentPeriod])

  const [dateRange, setDateRange] = useState<DateRangeValue>(() =>
    parseMetricsDateRange()
  )
  const [quickRange, setQuickRange] = useState<string | undefined>(
    parseMetricsQuickRange
  )

  // ── Section collapse state (covers all 8 sections across both tabs) ──────
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() =>
    parseMetricsCollapsed()
  )

  const sectionExpanded = useMemo(
    () => buildSectionExpandedMap(collapsedSections),
    [collapsedSections]
  )

  const handleSectionExpandChange = useCallback(
    (id: string, expanded: boolean) => {
      setCollapsedSections((prev) => {
        const next = new Set(prev)
        if (expanded) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
    },
    []
  )

  // ── Payment tab toggles ─────────────────────────────────────────────────
  const [showAllMakers, setShowAllMakers] = useState<boolean>(() => {
    return parseMetricsShowAll().showAllMakers
  })
  const [showAllEscalation, setShowAllEscalation] = useState<boolean>(() => {
    return parseMetricsShowAll().showAllEscalation
  })

  // ── Customer tab selections ─────────────────────────────────────────────
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(
    parseMetricsCompanies
  )
  const [selectedCarriers, setSelectedCarriers] =
    useState<string[]>(parseMetricsCarriers)

  // ── Bot tab state ───────────────────────────────────────────────────────
  const [botView, setBotView] = useState<DashboardView>(() => {
    return parseMetricsBotState().botView as DashboardView
  })
  const [botCarrierId, setBotCarrierId] = useState<CarrierIdSelection>(() => {
    const { botCarrierId: id } = parseMetricsBotState()
    return id as CarrierIdSelection
  })

  // ── URL sync ────────────────────────────────────────────────────────────
  useEffect(() => {
    syncMetricsUrl({
      tab: activeTab,
      period: paymentPeriod,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      quickRange,
      collapsed: collapsedSections,
      showAllMakers,
      showAllEscalation,
      companies: selectedCompanyIds,
      carriers: selectedCarriers,
      botView,
      botCarrierId,
    })
  }, [
    activeTab,
    paymentPeriod,
    dateRange.fromDate,
    dateRange.toDate,
    quickRange,
    collapsedSections,
    showAllMakers,
    showAllEscalation,
    selectedCompanyIds,
    selectedCarriers,
    botView,
    botCarrierId,
  ])

  const dateRangeValid = isDateRangeValid(dateRange.fromDate, dateRange.toDate)

  const metricsRequest = useMemo(() => {
    if (!dateRangeValid) return null
    return {
      ...(dateRange.fromDate
        ? { fromDate: startOfDayPST(dateRange.fromDate) }
        : {}),
      ...(dateRange.toDate ? { toDate: endOfDayPST(dateRange.toDate) } : {}),
      month: paymentAssignmentPeriod.month,
      year: paymentAssignmentPeriod.year,
    }
  }, [
    dateRange.fromDate,
    dateRange.toDate,
    dateRangeValid,
    paymentAssignmentPeriod,
  ])

  const {
    data: paymentMetricsData,
    isLoading: isMetricsLoading,
    isFetching: isMetricsFetching,
    refetch: refetchMetrics,
  } = usePaymentMetricsQuery(metricsRequest, { refetchOnWindowFocus: false })

  const queryClient = useQueryClient()
  const isBotFetching =
    useIsFetching({ queryKey: ['payment-bot-dashboard'] }) > 0

  const handleRefresh = useCallback(() => {
    if (isDateRangeTab) {
      refetchMetrics()
    } else if (activeTab === 'bot') {
      queryClient.invalidateQueries({ queryKey: ['payment-bot-dashboard'] })
    }
  }, [isDateRangeTab, activeTab, refetchMetrics, queryClient])

  const totalRecords = paymentMetricsData?.totalActivityCount ?? 0

  const tabItems = useMemo(
    () => [
      {
        key: 'payment',
        label: 'Payment Management Metrics',
        children: (
          <PaymentMetrics
            data={paymentMetricsData}
            loading={isMetricsLoading}
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            paymentDate={currentDate}
            showAllMakers={showAllMakers}
            onShowAllMakersChange={setShowAllMakers}
            showAllEscalation={showAllEscalation}
            onShowAllEscalationChange={setShowAllEscalation}
            sectionExpanded={sectionExpanded}
            onSectionExpandChange={handleSectionExpandChange}
          />
        ),
      },
      {
        key: 'customer',
        label: 'Customer Management Metrics',
        children: (
          <CustomerMetrics
            paymentMetricsData={paymentMetricsData}
            loading={isMetricsLoading}
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            month={paymentAssignmentPeriod.month}
            year={paymentAssignmentPeriod.year}
            dateRangeValid={dateRangeValid}
            isActive={activeTab === 'customer' && isCheckerOrViewer}
            selectedCompanyIds={selectedCompanyIds}
            onCompanyIdsChange={setSelectedCompanyIds}
            selectedCarriers={selectedCarriers}
            onSelectedCarriersChange={setSelectedCarriers}
            companyExpanded={sectionExpanded[METRIC_SECTION.COMPANY]}
            onCompanyExpandedChange={(v) =>
              handleSectionExpandChange(METRIC_SECTION.COMPANY, v)
            }
            carrierExpanded={sectionExpanded[METRIC_SECTION.CARRIER]}
            onCarrierExpandedChange={(v) =>
              handleSectionExpandChange(METRIC_SECTION.CARRIER, v)
            }
          />
        ),
      },
      {
        key: 'bot',
        label: 'Bot Performance Metric',
        children: (
          <PaymentBotDashboardTab
            month={paymentAssignmentPeriod.month}
            year={paymentAssignmentPeriod.year}
            view={botView}
            onViewChange={setBotView}
            detailCarrierId={botCarrierId}
            onDetailCarrierIdChange={setBotCarrierId}
          />
        ),
      },
    ],
    [
      activeTab,
      isCheckerOrViewer,
      paymentMetricsData,
      isMetricsLoading,
      dateRange.fromDate,
      dateRange.toDate,
      currentDate,
      paymentAssignmentPeriod,
      dateRangeValid,
      showAllMakers,
      showAllEscalation,
      sectionExpanded,
      handleSectionExpandChange,
      selectedCompanyIds,
      selectedCarriers,
      botView,
      botCarrierId,
    ]
  )

  return (
    <PageWrapper>
      <Page
        title="Payment Assignment Metrics"
        maxWidth="1600px"
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Finance', path: FINANCE_PATH },
              {
                title: 'Payment Assignment',
                path: `${FINANCE_PATH}/payment-assignment`,
              },
              { title: 'Metrics' },
            ]}
          />
        }
      >
        <Layout style={{ backgroundColor: '#f6f9fc' }}>
          <Content style={{ padding: '24px' }}>
            <Spin spinning={isRoleLoading}>
              <ToolbarRow>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <DatePicker
                    picker="month"
                    value={currentDate}
                    onChange={handleDateChange}
                    style={{ width: 180 }}
                    format="MMMM YYYY"
                  />
                  {/* Date Range filter is shown only for transaction metrics tabs (payment & customer) */}
                  {isDateRangeTab ? (
                    <DateRangeFilter
                      value={dateRange}
                      onChange={setDateRange}
                      loading={isMetricsFetching}
                      onRefresh={handleRefresh}
                      timezone={TIMEZONE_PST}
                      defaultQuickRange={quickRange ?? 'Realtime'}
                      onQuickRangeChange={setQuickRange}
                    />
                  ) : (
                    /* Show a standalone Refresh button for the Bot tab since it doesn't use the DateRange filter */
                    <Button
                      icon={<ReloadOutlined />}
                      loading={isBotFetching}
                      onClick={handleRefresh}
                    >
                      Refresh
                    </Button>
                  )}
                </div>
                {/* Total logs count applies only to transaction metrics tabs */}
                {isDateRangeTab && (
                  <Text>
                    Total activity logs: <Text strong>{fmt(totalRecords)}</Text>
                  </Text>
                )}
              </ToolbarRow>

              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                type="card"
                items={tabItems}
              />
            </Spin>
          </Content>
        </Layout>
      </Page>
    </PageWrapper>
  )
}
